/**
 * CU estimates from profiling a single claim (shared ATAs + one burn):
 * - Full first claim: ~62k CUs
 * - Burn instruction only: ~37k CUs
 *
 * Additional claims in the same tx reuse shared ATAs (idempotent) and only pay
 * per-ticket ticket-ATA + burn (~20k + 37k, conservative).
 */
export const BATCH_CLAIM_CU = {
  FIRST_CLAIM_TOTAL: 62_000,
  BURN_ONLY: 37_000,
  TICKET_ATA_ESTIMATE: 20_000,
  TX_OVERHEAD: 15_000,
  /** Used for batch claims (single-tx default in sendTx is 250k). */
  BATCH_COMPUTE_LIMIT: 1_400_000,
  LEGACY_DEFAULT_COMPUTE_LIMIT: 250_000,
} as const;

const MARGINAL_CLAIM_CU =
  BATCH_CLAIM_CU.BURN_ONLY + BATCH_CLAIM_CU.TICKET_ATA_ESTIMATE;

/** Hard cap to reduce legacy tx size risk (many account metas). */
export const BATCH_CLAIM_MAX_PER_TX_CAP = 15;

export function estimateMaxClaimsPerTx(
  computeLimit: number = BATCH_CLAIM_CU.BATCH_COMPUTE_LIMIT
): number {
  const available =
    computeLimit - BATCH_CLAIM_CU.FIRST_CLAIM_TOTAL - BATCH_CLAIM_CU.TX_OVERHEAD;
  if (available <= 0) return 1;
  const additional = Math.floor(available / MARGINAL_CLAIM_CU);
  return Math.min(BATCH_CLAIM_MAX_PER_TX_CAP, 1 + additional);
}

export function chunkTicketsForBatchClaim<T>(tickets: T[]): T[][] {
  const maxPerTx = estimateMaxClaimsPerTx();
  const chunks: T[][] = [];
  for (let i = 0; i < tickets.length; i += maxPerTx) {
    chunks.push(tickets.slice(i, i + maxPerTx));
  }
  return chunks;
}
